/**
 * EventsIndex — id + range lookups over event-shaped cells,
 * backed by two interval trees (UTC-mse for partial-day,
 * DSE for full-day) and client-side RRULE expansion.
 *
 * Port of lona-js's `indices/events-index.ts` with the following
 * adaptations:
 *   - No `RowCellCache` / `TypedApi`. The facade (`EventsFacade`)
 *     owns the index and forwards mutations through
 *     `row.setCell` / `row.clearCell`. No network calls leak
 *     into tento/tento-lona-js/sheets.
 *   - Consumes canonical `CalendarEvent` values from `dtype.ts`. The
 *     normalizing backend decodes compact wire forms into
 *     canonical shape before cells reach the facade.
 *   - `populate` and `apply` are the only mutation paths: the facade
 *     hands either its row's whole events or one change set.
 *   - Emits the same `TimepointsChanged` / `ViewportsInvalidated`
 *     invalidations as lona-js.
 */

import { EventId } from "../ids/ids.ts";
import type { Option } from "../core/option.ts";
import {
  DateTime,
  DurationMs,
  exclusionsOfLines,
  type GoogleEventGenerator,
  isExcludedInstance,
  MsSinceEpoch,
  NaiveDate,
  TimezoneRegion,
  Tzname,
  Utc,
} from "@tento-chrono";
import { AVLTree } from "@tento-core/avl-tree";
import { nonnull } from "@tento-core/assert";

import type { CalendarEvent } from "../core/dtype.ts";
import type { EventOrigin } from "../tx/event-origin.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import {
  Actor,
  type InvalidationSink,
  TimepointsChanged,
  ViewportsInvalidated,
} from "../tx/invalidation.ts";
import { defineShape, type Shape } from "../core/shape.ts";
import {
  type EventItem,
  EventItemAllDay,
  EventItemTimed,
  type EventRef,
  RecurrenceInfo,
} from "./events/event-item.ts";
import { elementAt } from "../core/checked-index.ts";
import { TimeMembership } from "../render/time-membership.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

export { RecurrenceInfo };
export type { EventItem, EventRef };
export { EventItemAllDay, EventItemTimed };

/** The loaded region of the zone `name` names; `null` when not loaded or not a zone. */
function loadedRegion(name: string): Option<TimezoneRegion> {
  const tzname = Tzname.parseOpt(name);
  return tzname == null ? null : TimezoneRegion.getLoaded(tzname);
}

/**
 * Synchronously decide whether a recurring master's OWN DTSTART instant is
 * excluded by one of its EXDATEs — the same per-instance match the async
 * `GoogleEventGenerator` makes (its `startDateExcluded`; chrono
 * `exclusionsOfLines` / `isExcludedInstance`, RFC 5545 §3.8.5.1: an EXDATE
 * removes the instance at its DATE-TIME, a DATE value the whole day), but
 * without building the generator. A master renders its own first-occurrence slot directly (the
 * expander skips re-generating DTSTART), so EXDATE on DTSTART has nothing else
 * to suppress it. Every renderable read surface that can surface the raw master
 * cell must consult this: the per-row `EventsIndex` ingest chokepoint AND the
 * aggregate calendar union's raw variant-walk (which reads stored cells and
 * never touches the index). Returns false (don't suppress) when the source
 * timezone isn't loaded yet — the async `expandRecurrenceUntil` is the fallback
 * once it loads it.
 */
export function recurringMasterDtstartExcluded(master: CalendarEvent): boolean {
  if (master.start == null) return false;
  if (master.recurrence == null || master.recurringEventId != null) return false;
  if (!master.recurrence.some((line) => line.startsWith("EXDATE"))) return false;
  const tz = master.startTimeZone
    ? loadedRegion(master.startTimeZone)
    : TimezoneRegion.UTC;
  if (tz == null) return false; // tz not loaded yet (or not a zone) → defer to async expander
  const excl = exclusionsOfLines(master.recurrence, tz.fullname, (name) => TimezoneRegion.getLoaded(name));
  if (excl == null) return false; // an EXDATE's zone not loaded yet → defer to async expander
  const start = master.start;
  return isExcludedInstance(excl, tz.toTz(start).ndt, start);
}

/**
 * Structural probe for canonical `CalendarEvent` values. Requires an id,
 * title, and DateTime-like `start` + `end`. A FormulaDataSource
 * synthesizing this shape matches without any tagging.
 */
export const EVENT_SHAPE: Shape<CalendarEvent> = deepFrozen(defineShape<CalendarEvent>(
  "event/v1",
  (value): value is CalendarEvent => {
    if (!value || typeof value !== "object") return false;
    if (!("id" in value) || typeof value.id !== "string") return false;
    if (!("title" in value) || typeof value.title !== "string") return false;
    const hasMse = (x: unknown): boolean =>
      typeof x === "object" && x !== null && "mse" in x && typeof x.mse === "number";
    return "start" in value && hasMse(value.start) && "end" in value && hasMse(value.end);
  },
));

/**
 * Every event-shaped value owned by `row`, read across BOTH cell lanes — the
 * semantic date lane (all-day events) and the timepoint lane (timed events) —
 * through `SourceRow.peekCells`, deduped by event id. After the
 * events-spine substrate deconfliation a TIMED event lives only in the
 * datetime/custom lane, so any reader scanning the date lane alone silently
 * drops timed events. Route
 * every event-cell read through here (the facade, the list aggregator, the
 * calendar-list provider) so the calendar derives from one authoritative
 * representation per event regardless of which lane holds it.
 */
export function eventValues(
  row: SourceRow,
  range: Option<NaiveDate.Range>,
): CalendarEvent[] {
  const byId = new Map<string, CalendarEvent>();
  // One read through the row's single door: date-keyed cells first, then the
  // timepoint cells over `range`'s UTC day bounds — a later timepoint cell
  // wins the id dedupe, as it always has.
  const cells = row.peekCells(range);
  for (const cell of cells) {
    if (EVENT_SHAPE.probe(cell.value)) byId.set(cell.value.id, cell.value);
  }
  return [...byId.values()];
}

export interface EventsIndexDiagnostics {
  /** Total canonical events fed into the last populate. */
  probed: number;
  /** Events that became partial-day or full-day items. */
  accepted: number;
  /** Events missing time fields — dropped on ingest. */
  dropped: number;
}

/**
 * Minimal sink the index needs. The concrete `Sheet`'s
 * `invalidationSink` satisfies this shape directly.
 */
type EventsInvalidationSink = Pick<InvalidationSink, "emit">;

/**
 * The index behind `EventsFacade`: a row's events by id and time, with
 * recurrences expanded.
 */
export class EventsIndex {
  /** Raw canonical events by id — needed for mutations and
   *  expansion lookups. */
  readonly #rawById = new Map<EventId, CalendarEvent>();

  /** Hydrated partial-day items by id. */
  readonly #baseIndex = new Map<string, EventItemTimed>();
  readonly #rangeIndex = new AVLTree<
    AVLTree.Node.Many<string, EventItemTimed>
  >();

  /**
   * While set (`#inBulk`), `#ingestEvent` collects tree nodes here instead of
   * inserting each, and the scope inserts them at once (`insertBulk`): the
   * same nodes and values as one-by-one inserts. Only synchronous scopes that
   * read neither tree set it.
   */
  #pendingTimed: Option<AVLTree.Node.Many<string, EventItemTimed>[]> = null;
  #pendingAllDay: Option<AVLTree.Node.Many<string, EventItemAllDay>[]> = null;

  /** Hydrated full-day items by id. */
  readonly #allDayBaseIndex = new Map<string, EventItemAllDay>();
  readonly #allDayIndex = new AVLTree<
    AVLTree.Node.Many<string, EventItemAllDay>
  >();

  /** Per-master RRULE generator cache, with the `#recurrenceSig` it was
   *  built for: a master whose rule changed in place never reuses it. */
  readonly #recurrence = new Map<string, { sig: string; gen: GoogleEventGenerator }>();
  /**
   * Bumps on every `populate()` and every `apply` that touches a recurring
   * master or override. `expandRecurrenceUntil` captures it
   * and, after each await, writes nothing (generators, occurrences, caches,
   * horizon) when the masters changed meanwhile: its masters belong to the
   * generation it started in.
   */
  #generation = 0;
  /** The current generation (see `#generation`): an expansion that started
   *  in an older one committed nothing. */
  get generation(): number {
    return this.#generation;
  }

  /** The masters may have changed: supersede a pending expansion and let the
   *  next one run again (cached generators and occurrence dedupe keep it
   *  cheap and idempotent). */
  #mastersChanged(): void {
    this.#generation++;
    this.#lastRecurrenceUntil = null;
  }
  readonly #recurrToChildrenMap = new Map<EventId, Set<MsSinceEpoch>>();
  #lastRecurrenceUntil: Option<NaiveDate> = null;
  /** The synthetic occurrence ids each master's expansion (or cached
   *  re-ingest) added, and each one's master: what `apply` withdraws when a
   *  master changes or a stored override takes an occurrence's place. */
  readonly #synthetic = new Map<EventId, Set<EventId>>();
  readonly #syntheticMaster = new Map<EventId, EventId>();
  /** Every stored (non-synthetic) event by id, and whether ingest placed it
   *  (`false`: dropped for want of times). The diagnostics count these. */
  readonly #storedById = new Map<EventId, boolean>();
  /** The events `populate` ingested and has not yet recorded in `#storedById`. */
  #storedPending: Option<readonly CalendarEvent[]> = null;

  /**
   * The stored-event record. `populate` (a facade's first read) only
   * ingests; the record is built on its first use (an edit, a provenance
   * read, the diagnostics), so a read-only index never pays for it.
   */
  get #stored(): Map<EventId, boolean> {
    const pending = this.#storedPending;
    if (pending != null) {
      this.#storedPending = null;
      for (const ev of pending) if (ev != null) this.#storedById.set(ev.id, EventsIndex.#placeable(ev));
    }
    return this.#storedById;
  }

  /** Whether `#ingestEvent` places `ev` in a bucket (it has dates or times); its answer, without ingesting. */
  static #placeable(ev: CalendarEvent): boolean {
    return (ev.startDate != null && ev.endDate != null) || (ev.start != null && ev.end != null);
  }

  /**
   * Per-master cache of the already-generated synthetic-occurrence starts
   * (+ the recurrence rule they came from). UNLIKE `recurrence` (the async
   * generators) and `recurrToChildrenMap`, this SURVIVES `populate()`. An
   * unrelated cell edit re-populates the index — clearing synthetics and the
   * generators — and the real (async) re-expansion lands a frame later, so
   * already-on-screen weeks would vanish until it does ("skips weeks for a
   * bit"). On re-populate we re-ingest these cached instances SYNCHRONOUSLY,
   * closing that gap. Invalidated per-master when the master's recurrence
   * string changes or the master disappears.
   */
  readonly #recurrenceOccCache = new Map<
    EventId,
    { sig: string; occs: DateTime<Utc>[]; cached: Set<MsSinceEpoch> }
  >();
  /** Horizon the occurrence cache covers, restored into
   *  `lastRecurrenceUntil` after a re-populate re-ingests from cache. */
  #cachedRecurrenceUntil: Option<NaiveDate> = null;


  static readonly ACTOR = new Actor("EventsIndex");

  /**
   * Monotonically-increasing version counter. Bumps on every
   * `populate()` and `expandRecurrenceUntil()` (the only paths
   * that can change query results). Consumers cache derived
   * arrays (e.g. `partialEventsInRange`) keyed on
   * `(rowId, range, version)` and skip recomputation when the
   * version hasn't advanced — returning identity-equal arrays so
   * downstream reconcile short-circuits per leaf.
   *
   * Note: `apply`, the expansion's inserts (`emitInsert`) and the
   * master-slot suppression (the fine-grained mutation paths) also bump because they alter
   * the indexed state. Increments are coarse — any change bumps
   * the version, even if the change doesn't affect a particular
   * cached query's range. Callers are expected to gate their
   * cache by `version` AND their own range key.
   */
  #revision: number = 0;
  get version(): number {
    return this.#revision;
  }

  readonly #origin: EventOrigin;
  readonly #invalidation: EventsInvalidationSink;
  constructor(
    invalidation: EventsInvalidationSink,
    /**
     * The opaque origin of this index's events. Stamped onto every
     * constructed `EventItem` via `ingestEvent` so consumers route
     * mutations back to the writable source row without re-walking the
     * sheet tree. Carried from the source row, never derived.
     */
    origin: EventOrigin,
  ) {
    this.#invalidation = invalidation;
    this.#origin = origin;
  }

  // ── Build + populate ──────────────────────────────────────────────

  /**
   * Drop + rebuild from a canonical event stream. Callers pass
   * already-probed events (the facade filters via `EVENT_SHAPE`
   * before calling).
   */
  populate(events: readonly CalendarEvent[]): void {
    this.#generation++;
    this.#rawById.clear();
    this.#baseIndex.clear();
    this.#rangeIndex.clear();
    this.#allDayBaseIndex.clear();
    this.#allDayIndex.clear();
    this.#recurrence.clear();
    this.#recurrToChildrenMap.clear();
    this.#synthetic.clear();
    this.#syntheticMaster.clear();
    this.#storedById.clear();
    this.#lastRecurrenceUntil = null;

    // One event per id, the last one given (as `apply` keeps the last put):
    // a row's cells may carry an id twice, and two tree nodes for one id
    // would answer it twice.
    const byId = new Map<EventId, CalendarEvent>();
    for (const ev of events) if (ev != null) byId.set(ev.id, ev);
    this.#inBulk(() => {
      for (const ev of byId.values()) this.#ingestEvent(ev, null);
      this.#storedPending = [...byId.values()];
      // Re-ingest already-expanded recurrence instances synchronously (the
      // occurrence cache survived the clears above) so on-screen weeks don't
      // blank out while the async re-expansion catches up.
      this.#reingestCachedRecurrence();
    });
    this.#revision++;
  }

  /** Run `body` (synchronous, reading neither tree) with tree inserts collected, then insert them at once. */
  #inBulk(body: () => void): void {
    if (this.#pendingTimed != null) {
      body();
      return;
    }
    const timed: AVLTree.Node.Many<string, EventItemTimed>[] = [];
    const allDay: AVLTree.Node.Many<string, EventItemAllDay>[] = [];
    this.#pendingTimed = timed;
    this.#pendingAllDay = allDay;
    try {
      body();
    } finally {
      this.#flushPending();
      this.#pendingTimed = null;
      this.#pendingAllDay = null;
    }
  }

  /** Insert the nodes a bulk scope collected so far (before anything removes from a tree). */
  #flushPending(): void {
    const timed = this.#pendingTimed;
    const allDay = this.#pendingAllDay;
    if (timed != null && timed.length > 0) {
      this.#pendingTimed = [];
      AVLTree.Node.Many.insertBulk(this.#rangeIndex, timed);
    }
    if (allDay != null && allDay.length > 0) {
      this.#pendingAllDay = [];
      AVLTree.Node.Many.insertBulk(this.#allDayIndex, allDay);
    }
  }

  /**
   * Signature of everything that determines a master's synthetic-occurrence
   * START times: the DTSTART instant, the recurrence rule (incl. EXDATE
   * lines), and the source timezone. The occurrence cache is keyed on this,
   * so editing any of them invalidates it (a stale cache would otherwise
   * re-ingest occurrences at the old times).
   */
  #recurrenceSig(ev: CalendarEvent): string {
    return [
      ev.start != null ? String(ev.start.mse) : "",
      (ev.recurrence ?? []).join(","),
      ev.startTimeZone ?? "",
    ].join("|");
  }

  /**
   * Synchronously re-materialize the recurrence instances captured in
   * `recurrenceOccCache` after a `populate()` clear. For each cached master
   * still present with an unchanged signature, recreate its synthetic
   * instances from the cached occurrence starts; drop the cache entry when
   * the master is gone or its signature changed (the async expander rebuilds).
   */
  #reingestCachedRecurrence(): void {
    if (this.#recurrenceOccCache.size === 0) return;

    // The horizon may only be restored if EVERY recurring master is covered
    // by a still-valid cache entry. A master that's new (no entry) or whose
    // rule changed (entry dropped below) must be left for the async expander,
    // so `lastRecurrenceUntil` (cleared by populate) stays null and doesn't
    // short-circuit that work.
    let fullyCovered = true;
    for (const ev of this.#rawById.values()) {
      if (ev.recurrence == null) continue;
      if (ev.recurringEventId != null) continue;
      if (ev.startDate != null) continue;
      const cached = this.#recurrenceOccCache.get(ev.id);
      if (!cached || this.#recurrenceSig(ev) !== cached.sig) {
        fullyCovered = false;
      }
    }

    for (const [masterId, cached] of this.#recurrenceOccCache) {
      const master = this.#rawById.get(masterId);
      if (
        !master ||
        master.recurrence == null ||
        this.#recurrenceSig(master) !== cached.sig
      ) {
        this.#recurrenceOccCache.delete(masterId);
        continue;
      }
      const durationMs = DurationMs.between(master.start.mse, master.end.mse);
      const existing = this.#recurrToChildrenMap.get(masterId) ??
        new Set<MsSinceEpoch>();
      this.#recurrToChildrenMap.set(masterId, existing);
      for (const occ of cached.occs) {
        const occMse = occ.mse;
        // Mirror expandRecurrenceUntil's dedup (master renders its own slot).
        if (occMse === master.start.mse) continue;
        const info = new RecurrenceInfo.Instance(masterId, occ);
        if (this.#rawById.has(info.id)) continue;
        if (existing.has(occMse)) continue;
        const synth: CalendarEvent = {
          ...master,
          id: info.id,
          start: occ,
          end: occ.add({ ms: durationMs }),
          recurringEventId: masterId,
          // As `expandRecurrenceUntil` builds it: the master's rule marks
          // the occurrence as recurring.
          recurrence: master.recurrence,
        };
        this.#ingestSynthetic(masterId, synth, info);
        existing.add(occMse);
      }
    }
    // Restore the covered horizon so a query within it doesn't kick a
    // redundant async re-expansion; queries beyond it still extend. Only safe
    // when every recurring master was cache-covered (else the async expander
    // must run for the uncovered/changed ones).
    if (fullyCovered) this.#lastRecurrenceUntil = this.#cachedRecurrenceUntil;
  }

  /**
   * Build a read-only index pre-populated with a fixed event
   * set and no live sink. Useful for null-state fixtures. Writes
   * throw. One event per id, as `populate` keeps it: a later event with
   * the same id replaces an earlier one (no item is placed twice).
   *
   * Contract: a seeded index answers the stored events only. It never
   * expands a recurring master (expansion is async and needs the live
   * index), so a master reads as its DTSTART slot alone. A constituent
   * that holds recurring masters must carry an events facade to show its
   * occurrences.
   */
  static seeded(
    events: readonly CalendarEvent[],
    origin: EventOrigin,
  ): EventsIndex {
    const idx = new EventsIndex({ emit: () => {} }, origin);
    const byId = new Map<EventId, CalendarEvent>();
    for (const ev of events) byId.set(ev.id, ev);
    for (const ev of byId.values()) idx.#stored.set(ev.id, idx.#ingestEvent(ev, null));
    return idx;
  }

  diagnostics(): EventsIndexDiagnostics {
    let accepted = 0;
    for (const placed of this.#stored.values()) if (placed) accepted++;
    return { probed: this.#stored.size, accepted, dropped: this.#stored.size - accepted };
  }

  // ── Recurrence expansion ──────────────────────────────────────────

  /**
   * Expand RRULE masters into synthetic instances up to `until`
   * (exclusive). Safe to call repeatedly; each master's generator
   * is cached after first expansion. Already-materialized
   * instances (same `<master>_<dt>` id) are skipped so we don't
   * clobber server-authoritative cells. `"announce"` (the default) publishes
   * the generated instances as an invalidation; `"silent"` leaves that to a
   * caller that announces them itself.
   *
   * A `populate` while a generator is being built supersedes the call: it
   * returns without writing anything into the rebuilt index (no generator,
   * occurrence, cache entry or horizon), so the next call expands the new
   * generation's masters from scratch.
   */
  async expandRecurrenceUntil(
    until: NaiveDate,
    invalidation: "announce" | "silent" = "announce",
  ): Promise<void> {
    if (
      this.#lastRecurrenceUntil &&
      this.#lastRecurrenceUntil.dse >= until.dse
    ) return;

    const generation = this.#generation;
    const generated: CalendarEvent[] = [];

    // Snapshot masters — the loop mutates `rawById` as it ingests
    // synthetic instances.
    const masters: CalendarEvent[] = [];
    for (const ev of this.#rawById.values()) {
      if (ev.recurrence == null) continue;
      if (ev.recurringEventId != null) continue; // instance, not master
      if (ev.startDate != null) continue; // full-day RRULEs TBD
      masters.push(ev);
    }

    // Build every missing generator first (the only awaits), then ingest all
    // occurrences synchronously in one bulk scope: the trees take the new
    // nodes at once instead of one insert per occurrence.
    const expanding: { master: CalendarEvent; sig: string; gen: GoogleEventGenerator }[] = [];
    for (const master of masters) {
      const sig = this.#recurrenceSig(master);
      const cachedGen = this.#recurrence.get(master.id);
      let gen = cachedGen?.sig === sig ? cachedGen.gen : null;
      if (!gen) {
        const item = new EventItemTimed(
          master,
          undefined,
          RecurrenceInfo.ofEvent(master),
          this.#origin,
        );
        const built = await item.makeRecurrenceGenerator();
        // Superseded: `master` and everything below belong to a generation
        // `populate` has already replaced.
        if (generation !== this.#generation) return;
        if (!built) continue;
        gen = built;
        this.#recurrence.set(master.id, { sig, gen });
      }
      expanding.push({ master, sig, gen });
    }

    this.#inBulk(() => {
      for (const { master, sig, gen } of expanding) {
        const occurrences = gen.generateUpToDate(until);
        const existing = this.#recurrToChildrenMap.get(master.id) ??
          new Set<MsSinceEpoch>();
        this.#recurrToChildrenMap.set(master.id, existing);

        // A master whose own DTSTART is EXDATE'd renders nothing at its first
        // slot: it's the renderer of that occurrence (skipped below), and EXDATE
        // only filters generated occurrences, not the raw master cell. Drop it
        // from the renderable buckets (kept in `rawById` so it still expands the
        // surviving occurrences). Without this, "edit/delete/move THIS" on the
        // first occurrence leaves the master lingering next to the fork.
        // tz-cold fallback: on a fresh load the timezone data isn't yet resolved,
        // so `ingestEvent`'s synchronous DTSTART-excluded check can't fire and
        // the master was ingested into the renderable buckets. The generator just
        // loaded the tz, so use its authoritative `startDateExcluded` to drop the
        // master here (emits, so an already-painted slot is removed). Idempotent
        // once `ingestEvent` has been suppressing it (no partial → no-op).
        if (gen.startDateExcluded) this.#suppressMasterSlot(master.id);

        // Occurrence cache (survives re-populate for synchronous re-ingest).
        // Reset it if the master's signature changed since it was built.
        let cache = this.#recurrenceOccCache.get(master.id);
        if (!cache || cache.sig !== sig) {
          cache = { sig, occs: [], cached: new Set() };
          this.#recurrenceOccCache.set(master.id, cache);
        }

        const durationMs = DurationMs.between(master.start.mse, master.end.mse);

        for (const occ of occurrences) {
          const occMse = occ.mse;
          // RRULE occurrences include DTSTART, which the master event already
          // renders. Skip it so the master's own slot isn't duplicated.
          if (occMse === master.start.mse) continue;
          // Every generated slot goes in the cache, an overridden one too: a
          // warm re-populate re-ingests from the cache and restores the
          // horizon, so a slot missing here would stay empty after its
          // override is removed (re-ingest skips a slot an override holds).
          if (!cache.cached.has(occMse)) {
            cache.cached.add(occMse);
            cache.occs.push(occ);
          }
          const info = new RecurrenceInfo.Instance(master.id, occ);
          if (this.#rawById.has(info.id)) continue;
          if (existing.has(occMse)) continue;

          const synth: CalendarEvent = {
            ...master,
            id: info.id,
            start: occ,
            end: occ.add({ ms: durationMs }),
            recurringEventId: master.id,
            // Carry the master's RRULE so the UI recognizes this occurrence as
            // recurring (the event-details panel reads `inner.recurrence` to show
            // the repeat rule; null here made a clicked instance say "does not
            // repeat"). `recurringEventId` keeps it out of master detection, so
            // it is never itself re-expanded.
            recurrence: master.recurrence,
          };
          this.#ingestSynthetic(master.id, synth, info);
          existing.add(occMse);
          generated.push(synth);
        }
      }
    });

    // Monotonic: a concurrent expansion to a nearer horizon that settles
    // later must not lower what a farther one already covered.
    if (!this.#lastRecurrenceUntil || until.dse > this.#lastRecurrenceUntil.dse) {
      this.#lastRecurrenceUntil = until;
    }
    this.#cachedRecurrenceUntil = until;

    if (invalidation === "announce" && generated.length > 0) {
      const items: EventItem[] = [];
      for (const ev of generated) {
        const p = this.#baseIndex.get(ev.id);
        if (p) items.push(p);
      }
      if (items.length > 0) this.#emitInsert(items);
    }
  }

  /**
   * `recurrenceInfo` lets synthetic recurrence instances carry their
   * `RecurrenceInfo.Instance` so the UI recognizes them as occurrences of
   * a master (right-click delete/edit, hydration). Real cells pass `null`
   * → the EventItem derives Parent/Instance from its own id
   * (`RecurrenceInfo.ofEvent`).
   */
  #ingestEvent(
    ev: CalendarEvent,
    recurrenceInfo: Option<RecurrenceInfo>,
  ): boolean {
    this.#rawById.set(ev.id, ev);
    if (ev.startDate != null && ev.endDate != null) {
      const item = new EventItemAllDay(
        ev,
        recurrenceInfo ?? RecurrenceInfo.ofEvent(ev),
        this.#origin,
      );
      this.#allDayBaseIndex.set(ev.id, item);
      const node = new AVLTree.Node.Many([item]);
      if (this.#pendingAllDay != null) this.#pendingAllDay.push(node);
      else this.#allDayIndex.insert(node);
      return true;
    }
    // Partial-day is the default — needs start + end DateTimes.
    if (ev.start != null && ev.end != null) {
      // A recurring master whose own DTSTART is EXDATE'd renders NOTHING at its
      // first slot: the master is the renderer of that occurrence (expansion
      // skips re-generating it), and EXDATE only filters generated occurrences,
      // not the raw master. Keep it in `rawById` (above) so it still drives
      // expansion of the surviving occurrences, but skip the renderable buckets.
      // This is the single ingest chokepoint, so every add path — populate,
      // `apply`, recurrence re-ingest — is covered
      // uniformly (after-the-fact removal missed re-adds and lingered the slot).
      // Synchronous; falls back to the `expandRecurrenceUntil` suppression when
      // the tz isn't loaded yet (see `recurringMasterDtstartExcluded`).
      if (recurringMasterDtstartExcluded(ev)) return true;
      const item = new EventItemTimed(
        ev,
        undefined,
        recurrenceInfo ?? RecurrenceInfo.ofEvent(ev),
        this.#origin,
      );
      this.#baseIndex.set(ev.id, item);
      const node = new AVLTree.Node.Many([item]);
      if (this.#pendingTimed != null) this.#pendingTimed.push(node);
      else this.#rangeIndex.insert(node);
      return true;
    }
    return false;
  }

  /** Ingest one synthetic occurrence of `masterId` and remember it as one. */
  #ingestSynthetic(masterId: EventId, synth: CalendarEvent, info: RecurrenceInfo): void {
    this.#ingestEvent(synth, info);
    let ids = this.#synthetic.get(masterId);
    if (ids == null) {
      ids = new Set();
      this.#synthetic.set(masterId, ids);
    }
    ids.add(synth.id);
    this.#syntheticMaster.set(synth.id, masterId);
  }

  // ── Incremental update ────────────────────────────────────────────

  /**
   * Bring the index to the event set `populate` would hold after removing
   * `changes.remove` and upserting `changes.put` (by id) — in work
   * proportional to the change, not to the row's history. After both reach
   * the same expansion horizon, every query answers what a fresh index
   * populated with the resulting set answers (members, items' fields and
   * order). `put`: stored events to upsert by id; `remove`: ids to remove.
   *
   * - An event that is not recurring (no rule, no master, no instance id)
   *   changes only its own buckets: the generation, and the horizon the
   *   masters are expanded to, hold.
   * - A recurring master that changed withdraws its occurrences: with its
   *   rule unchanged (a title, a calendar) they are rebuilt at once from the
   *   occurrence cache; with its rule changed or the master gone, its cache
   *   goes too. Any change touching recurrence starts a new generation
   *   (`#mastersChanged`) with fresh generators, as `populate` does, so a
   *   pending expansion writes nothing and the next one re-expands (the
   *   per-master occurrence sets keep that to the missing occurrences).
   * - A stored event with an occurrence's id (a materialized override)
   *   replaces the synthetic one; removing it lets the next expansion
   *   restore the occurrence.
   */
  apply(changes: { readonly put: readonly CalendarEvent[]; readonly remove: readonly EventId[] }): void {
    if (changes.put.length === 0 && changes.remove.length === 0) return;
    let recurrenceChanged = false;
    // The masters whose generators this change can invalidate; `all` when an
    // instance id names no master this index knows.
    const touched = new Set<string>();
    let all = false;
    const note = (ev: CalendarEvent): void => {
      if (!EventsIndex.#touchesRecurrence(ev)) return;
      recurrenceChanged = true;
      if (EventsIndex.#isExpandableMaster(ev)) touched.add(ev.id);
      const master = ev.recurringEventId ?? this.#syntheticMaster.get(ev.id) ??
        (ev.id.includes("_") ? ev.id.slice(0, ev.id.lastIndexOf("_")) : null);
      if (master != null) touched.add(master);
      else if (!EventsIndex.#isExpandableMaster(ev)) all = true;
    };
    for (const id of changes.remove) {
      const old = this.#unstore(id);
      if (old == null) continue;
      note(old);
      if (EventsIndex.#isExpandableMaster(old)) this.#withdrawOccurrences(old.id, "forget");
    }
    for (const ev of changes.put) {
      const old = this.#unstore(ev.id);
      const masterOf = this.#syntheticMaster.get(ev.id);
      if (masterOf != null) this.#withdrawSynthetic(masterOf, ev.id);
      this.#stored.set(ev.id, this.#ingestEvent(ev, null));
      note(ev);
      if (old != null) note(old);
      if (old != null && EventsIndex.#isExpandableMaster(old)) {
        const sameRule = EventsIndex.#isExpandableMaster(ev) && this.#recurrenceSig(old) === this.#recurrenceSig(ev);
        this.#withdrawOccurrences(old.id, sameRule ? "keep" : "forget");
        if (sameRule) this.#reingestCached(ev);
      }
    }
    if (recurrenceChanged) {
      // A generator yields each occurrence once (it keeps a cursor), so an
      // occurrence withdrawn here can only come back from a fresh one: drop
      // the touched masters' generators (an untouched master's occurrences
      // are all still held, so its generator owes nothing); the occurrence
      // cache keeps the re-expansion to the occurrences not already held.
      if (all) this.#recurrence.clear();
      else for (const master of touched) this.#recurrence.delete(master);
      this.#mastersChanged();
    }
    this.#revision++;
  }

  /** Whether `ev` takes part in recurrence: a rule, a master, or an instance id. */
  static #touchesRecurrence(ev: CalendarEvent): boolean {
    return ev.recurrence != null || ev.recurringEventId != null || ev.id.includes("_");
  }

  /** A timed recurring master `expandRecurrenceUntil` expands. */
  static #isExpandableMaster(ev: CalendarEvent): boolean {
    return ev.recurrence != null && ev.recurringEventId == null && ev.startDate == null;
  }

  /** Remove the stored event `id` from every bucket; its prior value, or
   *  `null` when no stored event has that id (a synthetic one is left). */
  #unstore(id: EventId): Option<CalendarEvent> {
    if (!this.#stored.has(id)) return null;
    const old = nonnull(this.#rawById.get(id), "EventsIndex: a stored event is held raw");
    this.#stored.delete(id);
    this.#dropFromBuckets(id);
    this.#rawById.delete(id);
    return old;
  }

  /** Drop `id`'s renderable item, if it has one (a suppressed master has none). */
  #dropFromBuckets(id: EventId): void {
    this.#flushPending();
    const partial = this.#baseIndex.get(id);
    if (partial != null) {
      this.#baseIndex.delete(id);
      this.#rangeIndex.delete(new AVLTree.Node.Many([partial]));
    }
    const full = this.#allDayBaseIndex.get(id);
    if (full != null) {
      this.#allDayBaseIndex.delete(id);
      this.#allDayIndex.delete(new AVLTree.Node.Many([full]));
    }
  }

  /** Withdraw one synthetic occurrence; the next expansion may add it again. */
  #withdrawSynthetic(masterId: EventId, id: EventId): void {
    const synth = this.#rawById.get(id);
    this.#dropFromBuckets(id);
    this.#rawById.delete(id);
    this.#synthetic.get(masterId)?.delete(id);
    this.#syntheticMaster.delete(id);
    if (synth?.start != null) this.#recurrToChildrenMap.get(masterId)?.delete(synth.start.mse);
  }

  /** Withdraw every occurrence of `masterId`; `"forget"` also drops its
   *  occurrence cache (its rule changed or it is gone). */
  #withdrawOccurrences(masterId: EventId, cache: "keep" | "forget"): void {
    for (const id of [...this.#synthetic.get(masterId) ?? []]) this.#withdrawSynthetic(masterId, id);
    this.#synthetic.delete(masterId);
    this.#recurrToChildrenMap.delete(masterId);
    this.#recurrence.delete(masterId);
    if (cache === "forget") this.#recurrenceOccCache.delete(masterId);
  }

  /** Re-ingest `master`'s cached occurrences (its rule unchanged), as
   *  `populate` does for every master: the occurrences carry its new fields. */
  #reingestCached(master: CalendarEvent): void {
    const cached = this.#recurrenceOccCache.get(master.id);
    if (cached == null || cached.sig !== this.#recurrenceSig(master)) return;
    const durationMs = DurationMs.between(master.start.mse, master.end.mse);
    const existing = new Set<MsSinceEpoch>();
    this.#recurrToChildrenMap.set(master.id, existing);
    for (const occ of cached.occs) {
      if (occ.mse === master.start.mse || existing.has(occ.mse)) continue;
      const info = new RecurrenceInfo.Instance(master.id, occ);
      if (this.#rawById.has(info.id)) continue;
      this.#ingestSynthetic(master.id, {
        ...master,
        id: info.id,
        start: occ,
        end: occ.add({ ms: durationMs }),
        recurringEventId: master.id,
        recurrence: master.recurrence,
      }, info);
      existing.add(occ.mse);
    }
  }

  /**
   * Whether the item the index holds under `id` is a stored event or an
   * occurrence its expansion generated; `null` when it holds none.
   */
  peekProvenance(id: EventId): Option<"stored" | "expanded"> {
    if (this.#stored.has(id)) return "stored";
    return this.#syntheticMaster.has(id) ? "expanded" : null;
  }

  // ── Queries ────────────────────────────────────────────────────────

  peek(id: EventId): Option<EventItem> {
    return this.#baseIndex.get(id) ?? this.#allDayBaseIndex.get(id) ?? null;
  }

  has(id: EventId): boolean {
    return this.#baseIndex.has(id) || this.#allDayBaseIndex.has(id);
  }

  peekOnDate(dt: DateTime<Utc>): EventItemTimed[] {
    const startOfDay = DateTime.fromDse(dt.date.dse, Utc);
    const start = startOfDay.mse;
    const end = start + 86_400_000; // 24h in ms
    return this.#belonging(start, end);
  }

  /**
   * The timed events that belong to `range` — lona's half-open day and
   * column membership (`TimeMembership`): an event ending exactly at the
   * range's start is not in it, nor is one starting exactly at its end, and
   * a zero-length event is in the one range containing its instant.
   */
  peekInRange(range: DateTime.Range<Utc>): EventItemTimed[] {
    return this.#belonging(range.start.mse, range.end.mse);
  }

  /**
   * The timed events that belong to `[start, end)` (`TimeMembership`, the
   * one day/range rule), in the index's one order (`ordered`).
   */
  #belonging(start: number, end: number): EventItemTimed[] {
    return EventsIndex.#overlapping(this.#rangeIndex.root ?? null, start, end)
      .filter((item) => TimeMembership.belongsMs(item.range.start, item.range.end, start, end));
  }

  /**
   * The all-day events whose days meet `range`. EventItemAllDay uses an
   * inclusive end (its constructor subtracts a day from the wire's
   * exclusive `endDate`), so the lookup is inclusive accordingly.
   */
  peekAllDayInRange(range: NaiveDate.Range): EventItemAllDay[] {
    return EventsIndex.#overlapping(this.#allDayIndex.root ?? null, range.start.dse, range.end.dse - 1);
  }

  /** Every all-day event, in the index's one order. */
  peekAllDayEvents(): EventItemAllDay[] {
    return [...this.#allDayBaseIndex.values()].sort(EventsIndex.#ordered);
  }

  /**
   * The items whose inclusive `range` meets `[lo, hi]`, in order: by start,
   * then by id (`#ordered`) — a function of the held set alone, never of the
   * order it was built or edited in, so an index kept by `apply` answers
   * exactly what one rebuilt by `populate` answers. An in-order walk that
   * skips a left subtree ending before `lo` and every subtree starting
   * after `hi`: O(log n + k).
   */
  static #overlapping<T extends EventItemTimed | EventItemAllDay>(
    root: AVLTree.Node.Many<string, T> | null | undefined,
    lo: number,
    hi: number,
  ): T[] {
    const out: T[] = [];
    const walk = (node: AVLTree.Node.Many<string, T> | null | undefined): void => {
      if (node == null || node.max < lo) return;
      walk(node.left);
      if (node.key > hi) return;
      const meeting = node.values.filter((v) => v.range.start <= hi && v.range.end >= lo);
      if (meeting.length > 1) meeting.sort(EventsIndex.#ordered);
      out.push(...meeting);
      walk(node.right);
    };
    walk(root);
    return out;
  }

  /** The index's one order: by start, then by id. */
  static #ordered(a: EventItemTimed | EventItemAllDay, b: EventItemTimed | EventItemAllDay): number {
    return a.range.start - b.range.start || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  }

  // ── Master-slot suppression ────────────────────────────────────────

  /**
   * Drop a recurring master from the RENDERABLE buckets while keeping it in
   * `rawById` so it still drives expansion. Used when the master's own DTSTART
   * occurrence is EXDATE'd: the master is the renderer of its first occurrence
   * (the expander skips re-generating it), so EXDATE on DTSTART has nothing
   * else to suppress that slot.
   *
   * Emits `TimepointsChanged` for the removed slot when
   * it actually removes something — `expandRecurrenceUntil` only announces
   * INSERTS (`generated.length > 0`), so a suppression that adds no new
   * occurrences would otherwise never reach the renderer and the master's card
   * would linger on screen (a reload, which rebuilds from scratch, hid this).
   * Idempotent: a no-op (and silent) once the master is already gone.
   */
  #suppressMasterSlot(masterId: string): void {
    const partial = this.#baseIndex.get(masterId);
    if (!partial) return;
    this.#baseIndex.delete(masterId);
    this.#rangeIndex.delete(new AVLTree.Node.Many([partial]));
    this.#revision++;
    this.#invalidation.emit(
      new TimepointsChanged(
        EventsIndex.ACTOR,
        EventsIndex.#timepointRange([partial]),
      ),
    );
  }

  // ── Invalidation emit ─────────────────────────────────────────────

  #emitInsert(items: readonly EventItem[]): void {
    this.#revision++;
    const partial: EventItemTimed[] = [];
    let anyFull = false;
    for (const it of items) {
      if (it instanceof EventItemTimed) partial.push(it);
      else if (it instanceof EventItemAllDay) anyFull = true;
    }
    if (partial.length > 0) {
      this.#invalidation.emit(
        new TimepointsChanged(
          EventsIndex.ACTOR,
          EventsIndex.#timepointRange(partial),
        ),
      );
    }
    if (anyFull) {
      this.#invalidation.emit(new ViewportsInvalidated(EventsIndex.ACTOR));
    }
  }

  static #timepointRange(
    events: readonly EventItemTimed[],
  ): { start: MsSinceEpoch; end: MsSinceEpoch } {
    const first = elementAt(events, 0, "EventsIndex.timepointRange: events");
    let min = first.time.mse.start;
    let max = first.time.mse.end;
    for (const event of events) {
      min = Math.min(min, event.time.mse.start);
      max = Math.max(max, event.time.mse.end);
      if (event.recurrenceInfo != null) {
        max = Number.MAX_SAFE_INTEGER;
      }
    }
    return { start: MsSinceEpoch.of(min), end: MsSinceEpoch.of(max) };
  }
}

